'use client';

import { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  acceptAll,
  acceptNecessary,
  getConsent,
  hasDecided,
  setConsent,
} from '@/lib/analytics/consent';

/**
 * Cookie banner — Linear/Stripe 2026 pattern.
 *
 * - Slim bottom strip on desktop (no full-screen overlay) so the landing
 *   page stays visible behind it — users read the value prop and decide
 *   in context rather than being interrupted.
 * - Customise opens a real settings dialog with switches (not a flipped
 *   panel inside the banner — easier to scan, keyboard-navigable).
 * - DSGVO-compliant: no "X close" without an explicit choice. The banner
 *   only renders while `hasDecided()` is false; closing the dialog returns
 *   to the banner so a decision is still required.
 * - Mobile: same slim strip, stacked buttons, with `safe-area-inset-bottom`
 *   so iPhone home-indicator doesn't overlap.
 */
export default function CookieBanner() {
  const t = useTranslations('components.cookieBanner');
  const [show, setShow] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    if (hasDecided()) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- one-shot mount sync
    setShow(true);
    const current = getConsent();
    if (current) {
      setAnalytics(current.analytics);
      setMarketing(current.marketing);
    }
  }, []);

  if (!show) return null;

  const handleAcceptAll = () => {
    acceptAll();
    setShow(false);
  };

  const handleAcceptNecessary = () => {
    acceptNecessary();
    setShow(false);
  };

  const handleSave = () => {
    setConsent({ analytics, marketing });
    setShow(false);
    setSettingsOpen(false);
  };

  return (
    <>
      {/* Slim bottom strip — no backdrop. Pinned via fixed + safe-area
          padding so it never overlaps the iOS home-indicator. */}
      <div
        role="dialog"
        aria-label={t('title')}
        className="fixed inset-x-0 bottom-0 z-50 border-t border-gray-200/80 bg-white/95 px-4 py-3 shadow-[0_-2px_12px_-2px_rgba(15,23,42,0.08)] backdrop-blur supports-[backdrop-filter]:bg-white/85 sm:px-6"
        style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
      >
        <div className="mx-auto flex max-w-5xl flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm leading-relaxed text-gray-700">
            {t('description')}{' '}
            <button
              type="button"
              onClick={() => setSettingsOpen(true)}
              className="font-medium text-blue-600 underline-offset-2 hover:underline"
            >
              {t('customize')}
            </button>
          </p>
          <div className="flex flex-shrink-0 gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={handleAcceptNecessary}
              className="flex-1 sm:flex-initial"
            >
              {t('acceptNecessary')}
            </Button>
            <Button size="sm" onClick={handleAcceptAll} className="flex-1 sm:flex-initial">
              {t('acceptAll')}
            </Button>
          </div>
        </div>
      </div>

      {/* Settings dialog — only opens when the user wants granular control.
          Switches instead of checkboxes (modern; matches the rest of admin). */}
      <Dialog
        open={settingsOpen}
        onOpenChange={(next) => {
          // Closing the dialog returns to the banner — the user still has
          // to make a choice somewhere; we don't honour "X close" as consent.
          if (!next) setSettingsOpen(false);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('title')}</DialogTitle>
            <DialogDescription>{t('description')}</DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <div className="flex items-start justify-between gap-3 rounded-lg border border-gray-200/80 px-3 py-2.5">
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-950">{t('necessaryTitle')}</p>
                <p className="mt-0.5 text-xs text-gray-500">{t('necessaryDesc')}</p>
              </div>
              <span className="flex-shrink-0 text-xs font-medium text-gray-500">
                {t('alwaysActive')}
              </span>
            </div>

            <label
              htmlFor="cookie-analytics"
              className="flex cursor-pointer items-start justify-between gap-3 rounded-lg border border-gray-200/80 px-3 py-2.5 transition-colors hover:bg-gray-50"
            >
              <div className="min-w-0">
                <Label htmlFor="cookie-analytics" className="text-sm font-medium text-gray-950">
                  {t('analyticsTitle')}
                </Label>
                <p className="mt-0.5 text-xs text-gray-500">{t('analyticsDesc')}</p>
              </div>
              <Switch
                id="cookie-analytics"
                checked={analytics}
                onCheckedChange={setAnalytics}
                className="flex-shrink-0"
              />
            </label>

            <label
              htmlFor="cookie-marketing"
              className="flex cursor-pointer items-start justify-between gap-3 rounded-lg border border-gray-200/80 px-3 py-2.5 transition-colors hover:bg-gray-50"
            >
              <div className="min-w-0">
                <Label htmlFor="cookie-marketing" className="text-sm font-medium text-gray-950">
                  {t('marketingTitle')}
                </Label>
                <p className="mt-0.5 text-xs text-gray-500">{t('marketingDesc')}</p>
              </div>
              <Switch
                id="cookie-marketing"
                checked={marketing}
                onCheckedChange={setMarketing}
                className="flex-shrink-0"
              />
            </label>
          </div>

          <DialogFooter className="flex-col gap-2 sm:flex-row">
            <Button variant="outline" onClick={handleAcceptNecessary} className="sm:flex-1">
              {t('acceptNecessary')}
            </Button>
            <Button onClick={handleSave} className="sm:flex-1">
              {t('saveSettings')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
